import { Link, NavLink } from "react-router-dom";
import {
  ArrowLeft,
  BarChart3,
  Combine,
  BellRing,
  Blocks,
  Building2,
  Coins,
  CreditCard,
  Factory,
  FileText,
  GitCompare,
  Globe,
  HandCoins,
  Home,
  Inbox,
  Mail,
  Network,
  Percent,
  Presentation,
  Receipt,
  ReceiptText,
  Repeat,
  ScrollText,
  Send,
  Sparkles,
  UserCheck,
  Users,
  Webhook,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "./ThemeProvider";
import { useRecordNavState, type RecordNavState } from "./RecordNav";
import logoDark from "../logo-dark.svg";
import logoLight from "../logo-light.svg";

type NavItem = { to: string; label: string; icon: LucideIcon; end?: boolean };
type NavGroup = { title?: string; items: NavItem[] };

// Detail pages (/organisations/:id …) highlight their parent through NavLink's prefix match.
const groups: NavGroup[] = [
  { items: [{ to: "/", label: "Overview", icon: Home, end: true }] },
  {
    title: "Accounts",
    items: [
      { to: "/clients", label: "Clients", icon: HandCoins },
      { to: "/organisations", label: "Organisations", icon: Building2 },
      { to: "/users", label: "Users", icon: Users },
      { to: "/demos", label: "Demos", icon: Presentation },
    ],
  },
  {
    title: "Records",
    items: [
      { to: "/invoices", label: "Invoices", icon: FileText },
      { to: "/bills", label: "Bills", icon: Receipt },
      { to: "/expenses", label: "Expenses", icon: CreditCard },
    ],
  },
  {
    title: "Insight",
    items: [
      { to: "/analytics", label: "Analytics", icon: BarChart3 },
      { to: "/three-way-match", label: "3-way match", icon: Combine },
      { to: "/user-activity", label: "User activity", icon: UserCheck },
      { to: "/match-groups", label: "Match groups", icon: GitCompare },
      { to: "/llm-extractions", label: "Bill extractions", icon: Sparkles },
      { to: "/receipt-llm-extractions", label: "Receipt extractions", icon: Sparkles },
    ],
  },
  {
    title: "Receivables",
    items: [
      { to: "/invoice-reminders", label: "Invoice reminders", icon: BellRing },
      { to: "/recurring-invoices", label: "Recurring invoices", icon: Repeat },
    ],
  },
  {
    title: "E-invoicing",
    items: [
      { to: "/received-e-invoices", label: "Received", icon: Inbox },
      { to: "/sent-e-invoices", label: "Sent", icon: Send },
      { to: "/e-invoicing-registrations", label: "Registrations", icon: Network },
    ],
  },
  {
    title: "Integrations",
    items: [
      { to: "/webhook-deliveries", label: "Webhook deliveries", icon: Webhook },
      { to: "/gmail-connections", label: "Gmail connections", icon: Mail },
    ],
  },
  {
    title: "Audit",
    items: [
      { to: "/modules-audit-logs", label: "Modules", icon: Blocks },
      { to: "/invoice-audit-logs", label: "Invoices", icon: ScrollText },
      { to: "/bill-audit-logs", label: "Bills", icon: ReceiptText },
    ],
  },
  {
    title: "Reference data",
    items: [
      { to: "/countries", label: "Countries", icon: Globe },
      { to: "/currencies", label: "Currencies", icon: Coins },
      { to: "/industries", label: "Industries", icon: Factory },
      { to: "/country-tax-rates", label: "Country tax table", icon: Percent },
    ],
  },
];

export function AdminSidebar({ onNavigate, className }: { onNavigate?: () => void; className?: string }) {
  const { theme } = useTheme();
  const logo = theme === "dark" ? logoDark : logoLight;
  const record = useRecordNavState();

  return (
    <aside className={cn("flex h-full w-56 shrink-0 flex-col border-r border-sidebar-border bg-sidebar", className)}>
      <Link to="/" onClick={onNavigate} className="flex h-12 shrink-0 items-center gap-2 px-4">
        <img src={logo} alt="" className="size-6" />
        <span className="font-semibold tracking-tight">Dolfin</span>
        <span className="rounded border border-sidebar-border px-1.5 py-px font-mono text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
          Admin
        </span>
      </Link>

      {record ? (
        <RecordNav record={record} onNavigate={onNavigate} />
      ) : (
      <nav className="flex-1 overflow-y-auto px-2 pb-4">
        {groups.map((group, i) => (
          <div key={group.title ?? i} className={cn(i > 0 && "mt-4")}>
            {group.title && (
              <p className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/80">
                {group.title}
              </p>
            )}
            <div className="flex flex-col gap-px">
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={onNavigate}
                  className={({ isActive }) => itemClass(isActive)}
                >
                  <item.icon className="size-4 shrink-0" />
                  <span className="truncate">{item.label}</span>
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
      )}
    </aside>
  );
}

const itemClass = (isActive: boolean) =>
  cn(
    "relative flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors",
    isActive
      ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground before:absolute before:inset-y-1.5 before:-left-2 before:w-0.5 before:rounded-full before:bg-sidebar-primary"
      : "text-muted-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-foreground",
  );

// A record page's sections, in place of the app-wide links (see RecordNav.tsx).
function RecordNav({ record, onNavigate }: { record: RecordNavState; onNavigate?: () => void }) {
  return (
    <nav className="flex flex-1 flex-col overflow-hidden" aria-label={`${record.kind} sections`}>
      <div className="shrink-0 px-2 pb-3">
        <Link
          to={record.back.to}
          onClick={onNavigate}
          className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-sidebar-accent/60 hover:text-sidebar-foreground"
        >
          <ArrowLeft className="size-4" />
          {record.back.label}
        </Link>
        <div className="mt-3 px-2">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/80">{record.kind}</p>
          <p className="mt-0.5 truncate font-semibold" title={record.title}>{record.title}</p>
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-px overflow-y-auto border-t border-sidebar-border px-2 pt-2 pb-4">
        {record.items.map((item) => (
          <a key={item.id} href={`#${item.id}`} onClick={onNavigate} className={itemClass(record.active === item.id)}>
            <item.icon className="size-4 shrink-0" />
            <span className="truncate">{item.label}</span>
          </a>
        ))}
      </div>
    </nav>
  );
}
